import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { organizationSchema, websiteSchema, localBusinessSchema, breadcrumbSchema, faqSchema } from '../utils/structuredData';

const WaitlistPage = () => {
  // Enhanced performance tracking and accessibility setup
  useEffect(() => {
    // Report Core Web Vitals for performance monitoring
    if ('web-vital' in window) {
      window.webVitals?.getCLS(console.log);
      window.webVitals?.getFID(console.log);
      window.webVitals?.getFCP(console.log);
      window.webVitals?.getLCP(console.log);
      window.webVitals?.getTTFB(console.log);
    }

    // Enhanced keyboard navigation for accessibility
    const handleKeyDown = (e) => {
      // Skip to main content on keyboard navigation
      if (e.ctrlKey || e.metaKey) return;
      
      // Escape key to close any open modals or menus
      if (e.code === 'Escape') {
        const focusedElement = document.activeElement;
        focusedElement?.blur();
      }
      
      // Improve focus management for form elements and interactive elements
      if (e.code === 'Space' || e.code === 'Enter') {
        const target = e.target;
        if (target.getAttribute('role') === 'button' && !target.href) {
          e.preventDefault();
          target.click();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // Load ConvertKit script
    const script = document.createElement('script');
    script.src = 'https://f.convertkit.com/ckjs/ck.5.js';
    script.async = true;
    document.head.appendChild(script);

    return () => {
      // Cleanup script on unmount
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Enhanced structured data for comprehensive SEO optimization
  const waitlistSchema = [
    {
      ...organizationSchema,
      // Ensure correct domain usage
      url: "https://emberimpact.com",
      logo: "https://customer-assets.emergentagent.com/job_ember-ui-update/artifacts/y5fpxlpp_Ember-Logo-website.png",
      description: "EMBER Impact Ventures transforms leaders through authentic community, research-backed practices, and innovative programs including The Crucible peer accelerator, Spark Moments™, and LIT Expeditions.",
      email: "hello@emberimpact.com"
    },
    {
      ...websiteSchema,
      url: "https://emberimpact.com",
      potentialAction: {
        "@type": "SearchAction",
        "target": "https://emberimpact.com/search?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Join EMBER Leadership Development Waitlist - Early Access to Transformational Programs",
      "description": "Get early access to transformational leadership programs including The Crucible peer accelerator, LIT Expeditions, DailyGLOW reflection practices, Heartlines connection cards, and EDGE x IMPACT innovation framework.",
      "url": "https://emberimpact.com/waitlist",
      "isPartOf": {
        "@type": "WebSite",
        "name": "EMBER Impact Ventures",
        "url": "https://emberimpact.com"
      },
      "mainEntity": {
        "@type": "Service",
        "name": "EMBER Leadership Development Programs Waitlist",
        "description": "Early access to comprehensive leadership development programs designed for transformational growth",
        "provider": {
          "@type": "Organization",
          "name": "EMBER Impact Ventures LLC",
          "url": "https://emberimpact.com"
        }
      },
      "offers": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Course",
            "name": "The Crucible: Peer Leadership Accelerator™",
            "description": "8-week peer leadership accelerator for exceptional leaders",
            "courseMode": "online",
            "duration": "P8W"
          },
          "availability": "https://schema.org/PreOrder",
          "priceCurrency": "USD",
          "price": "1997"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "LIT Expeditions™",
            "description": "4-7 day transformational leadership experiences"
          },
          "availability": "https://schema.org/PreOrder"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "SoftwareApplication",
            "name": "DailyGLOW™",
            "description": "Science-backed daily reflection app for sustainable growth",
            "applicationCategory": "LifestyleApplication"
          },
          "availability": "https://schema.org/PreOrder",
          "priceCurrency": "USD",
          "price": "0"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Product",
            "name": "Spark Moments™",
            "description": "Research-backed connection activities that build trust and performance"
          },
          "availability": "https://schema.org/InStock"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Product",
            "name": "Heartlines™",
            "description": "Curated questions for meaningful conversations"
          },
          "availability": "https://schema.org/PreOrder"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "EDGE x IMPACT™",
            "description": "Innovation mindsets and implementation framework for change leaders"
          },
          "availability": "https://schema.org/PreOrder"
        }
      ]
    },
    localBusinessSchema,
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Waitlist", url: "/waitlist" }
    ]),
    faqSchema([
      {
        question: "What programs are available on the EMBER waitlist?",
        answer: "The EMBER waitlist includes early access to The Crucible peer leadership accelerator, LIT Expeditions transformational experiences, DailyGLOW reflection app, Spark Moments connection activities, Heartlines conversation cards, and EDGE x IMPACT innovation framework."
      },
      {
        question: "What benefits do waitlist members receive?",
        answer: "Waitlist members receive early access to programs, exclusive pricing, weekly leadership insights and research updates, and community access to connect with fellow leaders before launch."
      },
      {
        question: "When will these programs launch?",
        answer: "Program launch dates vary. Waitlist members will be notified first when each program becomes available, with priority access and special pricing."
      }
    ])
  ];

  return (
    <>
      <SEOHead 
        title="Join the EMBER Waitlist - Early Access to Leadership Programs | EMBER Leadership"
        description="Be the first to access transformational leadership programs including The Crucible peer accelerator (8-week program), LIT Expeditions (4-7 day experiences), DailyGLOW reflection practices, Heartlines connection cards, and EDGE x IMPACT innovation framework. Get exclusive early access, special pricing, and leadership insights."
        keywords="ember waitlist, leadership development, the crucible, LIT expeditions, dailyglow, heartlines, edge impact, spark moments, leadership training, early access, peer accelerator, transformational leadership, leadership community, professional development, executive coaching"
        ogImage="https://images.unsplash.com/photo-1519389950473-47ba0277781c?q=80&w=2070&auto=format&fit=crop"
        canonicalUrl="/waitlist"
        structuredData={waitlistSchema}
      />

      {/* Preconnect for performance optimization - Mobile First */}
      <link rel="preconnect" href="https://f.convertkit.com" crossOrigin="" />
      <link rel="preconnect" href="https://app.kit.com" crossOrigin="" />
      <link rel="preconnect" href="https://images.unsplash.com" crossOrigin="" />
      <link rel="preconnect" href="https://fonts.googleapis.com" crossOrigin="" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      
      {/* Enhanced Skip Links for WCAG AAA Compliance */}
      <a 
        href="#main-content" 
        className="skip-nav"
        style={{
          position: 'absolute',
          left: '-10000px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
        onFocus={(e) => {
          e.target.style.position = 'absolute';
          e.target.style.left = '6px';
          e.target.style.top = '7px';
          e.target.style.width = 'auto';
          e.target.style.height = 'auto';
          e.target.style.overflow = 'visible';
          e.target.style.zIndex = '999999';
          e.target.style.padding = '8px 16px';
          e.target.style.background = '#FF5722';
          e.target.style.color = '#fff';
          e.target.style.textDecoration = 'none';
          e.target.style.borderRadius = '4px';
          e.target.style.fontWeight = '600';
        }}
        onBlur={(e) => {
          e.target.style.position = 'absolute';
          e.target.style.left = '-10000px';
          e.target.style.top = 'auto';
          e.target.style.width = '1px';
          e.target.style.height = '1px';
          e.target.style.overflow = 'hidden';
        }}
      >
        Skip to main content
      </a>
      <a 
        href="#waitlist-form" 
        className="skip-nav"
        style={{
          position: 'absolute',
          left: '-10000px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
        onFocus={(e) => {
          e.target.style.position = 'absolute';
          e.target.style.left = '200px';
          e.target.style.top = '7px';
          e.target.style.width = 'auto';
          e.target.style.height = 'auto';
          e.target.style.overflow = 'visible';
          e.target.style.zIndex = '999999';
          e.target.style.padding = '8px 16px';
          e.target.style.background = '#FF5722';
          e.target.style.color = '#fff';
          e.target.style.textDecoration = 'none';
          e.target.style.borderRadius = '4px';
          e.target.style.fontWeight = '600';
        }}
        onBlur={(e) => {
          e.target.style.position = 'absolute';
          e.target.style.left = '-10000px';
          e.target.style.top = 'auto';
          e.target.style.width = '1px';
          e.target.style.height = '1px';
          e.target.style.overflow = 'hidden';
        }}
      >
        Skip to waitlist form
      </a>

        {/* Enhanced Mobile-First CSS and Performance Optimizations */}
        <style>{`
          /* Mobile-first CSS Variables and Reset */
          :root {
            --ember-fire: #FF5722;
            --ember-flame: #FF7043;
            --ember-heat: #FF6F00;  
            --ember-spark: #FFB74D;
            --ember-glow: #FFCC80;
            --ember-depth: #673AB7;
            --ember-cool: #455A64;
            --ember-flow: #9C27B0;
            --text-primary: #212121;
            --text-secondary: #455A64;
            --neutral-warm: #FFF8F0;
            --neutral-light: #F5F5F5;
            --neutral-medium: #757575;
            --neutral-dark: #424242;
            --neutral-charcoal: #263238;
          }
          
          /* Mobile-first reset */
          * {
            box-sizing: border-box;
          }
          
          /* Enhanced focus management - WCAG AAA */
          *:focus {
            outline: 3px solid #FFC107 !important;
            outline-offset: 2px !important;
            border-radius: 2px;
          }
          
          /* Mobile-first container styles */
          .waitlist-page-container { 
            contain: layout style paint;
            will-change: transform;
            min-height: 100vh;
          }
          
          /* Performance optimizations */
          .waitlist-section {
            contain: layout style paint;
            transform: translateZ(0);
          }
          
          /* Mobile-first section spacing */
          section {
            contain: layout style paint;
            padding: 2rem 0; /* Mobile base padding */
          }
          
          /* Mobile-first typography */
          h1, h2, h3 { 
            line-height: 1.2;
            margin-bottom: 1rem;
          }
          
          h1 {
            font-size: 1.75rem; /* 28px mobile */
          }
          
          h2 {
            font-size: 1.5rem; /* 24px mobile */
          }
          
          h3 {
            font-size: 1.25rem; /* 20px mobile */
          }
          
          /* Mobile-first container and form optimization */
          .waitlist-container {
            padding-left: 1rem;
            padding-right: 1rem;
          }
          
          /* Form optimization with hardware acceleration */
          .convertkit-form {
            transform: translateZ(0);
            contain: layout style paint;
          }
          
          /* Skip Navigation - WCAG AAA */
          .skip-nav {
            background: var(--ember-fire);
            color: white;
            text-decoration: none;
            border-radius: 4px;
            font-weight: 600;
            font-size: 14px;
            transition: all 0.3s ease;
            z-index: 999999;
          }

          .skip-nav:focus {
            outline: 3px solid #FFC107;
            outline-offset: 3px;
          }
          
          /* Screen reader only content */
          .sr-only {
            position: absolute !important;
            width: 1px !important;
            height: 1px !important;
            padding: 0 !important;
            margin: -1px !important;
            overflow: hidden !important;
            clip: rect(0, 0, 0, 0) !important;
            white-space: nowrap !important;
            border: 0 !important;
          }
          
          /* Performance optimizations */
          img {
            content-visibility: auto;
            contain-intrinsic-size: 400px 300px;
            loading: lazy;
          }
          
          /* Float animation for background elements */
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
          
          .animate-float {
            animation: float 6s ease-in-out infinite;
          }
          
          /* ConvertKit form enhancements */
          .seva-form input, .seva-form button {
            min-height: 44px; /* WCAG minimum touch target */
            font-size: 16px; /* Prevent zoom on iOS */
          }
          
          .seva-form .formkit-checkbox + label {
            min-height: 44px;
            display: flex;
            align-items: center;
            cursor: pointer;
          }
          
          /* Tablet styles - 640px and up */
          @media (min-width: 640px) {
            section {
              padding: 3rem 0;
            }
            
            .waitlist-container {
              padding-left: 1.5rem;
              padding-right: 1.5rem;
            }
            
            h1 {
              font-size: 2.25rem; /* 36px tablet */
            }
            
            h2 {
              font-size: 1.875rem; /* 30px tablet */
            }
            
            h3 {
              font-size: 1.5rem; /* 24px tablet */
            }
          }
          
          /* Desktop styles - 1024px and up */
          @media (min-width: 1024px) {
            section {
              padding: 4rem 0;
            }
            
            h1 {
              font-size: 3rem; /* 48px desktop */
            }
            
            h2 {
              font-size: 2.25rem; /* 36px desktop */
            }
            
            h3 {
              font-size: 1.875rem; /* 30px desktop */
            }
          }
          
          /* Large desktop - 1440px and up */
          @media (min-width: 1440px) {
            h1 {
              font-size: 4rem; /* 64px large desktop */
            }
          }
          
          /* High contrast mode support - WCAG AAA */
          @media (prefers-contrast: high) {
            :root {
              --text-primary: #000000;
              --text-secondary: #000000;
              --ember-fire: #CC0000;
            }
            
            button, input, a, .bg-white {
              border: 2px solid currentColor !important;
            }
            
            .seva-form .formkit-checkbox + label {
              border: 2px solid #000000 !important;
            }
          }
          
          /* Reduced motion support - WCAG AAA */
          @media (prefers-reduced-motion: reduce) {
            *, *::before, *::after {
              animation-duration: 0.01ms !important;
              animation-iteration-count: 1 !important;
              transition-duration: 0.01ms !important;
              scroll-behavior: auto !important;
            }
            
            .animate-float {
              animation: none !important;
            }
          }
          
          /* Print styles */
          @media print {
            .skip-nav,
            button,
            .formkit-submit {
              display: none !important;
            }
            
            body {
              background: white !important;
              color: black !important;
            }
            
            * {
              background: white !important;
              color: black !important;
            }
          }
          
          /* Focus visible for modern browsers */
          button:focus-visible,
          input:focus-visible,
          a:focus-visible {
            outline: 3px solid #FFC107;
            outline-offset: 3px;
          }
          
          /* Enhanced accessibility for keyboard users */
          .skip-nav:focus,
          button:focus,
          input:focus,
          a:focus {
            z-index: 999999;
          }
          
          /* Error boundary styling */
          .error-boundary {
            padding: 2rem;
            text-align: center;
            background: #FFF3E0;
            border: 1px solid #FFB74D;
            border-radius: 8px;
            margin: 1rem;
          }
          
          /* ConvertKit form responsive improvements */
          @media (max-width: 640px) {
            .seva-form {
              padding: 1rem !important;
            }
            
            .seva-form .formkit-input {
              font-size: 16px !important; /* Prevent zoom on iOS */
            }
            
            .seva-form .formkit-checkbox + label {
              padding: 12px !important;
              font-size: 14px !important;
            }
          }
        `}</style>

        {/* Preconnect for performance */}
        <link rel="preconnect" href="https://f.convertkit.com" />
        <link rel="preconnect" href="https://app.kit.com" />
        <link rel="dns-prefetch" href="https://images.unsplash.com" />
        
        <Navigation />
        
        <main 
          id="main-content" 
          role="main" 
          className="waitlist-section"
        >
        
        {/* Hero Section */}
        <section className="pt-20 pb-12 sm:pb-16 relative overflow-hidden waitlist-container">
          {/* Background Elements */}
          <div className="absolute inset-0 opacity-10" aria-hidden="true">
            <div className="absolute top-20 left-20 w-40 h-40 bg-ember-spark rounded-full blur-3xl animate-float"></div>
            <div className="absolute bottom-20 right-20 w-32 h-32 bg-ember-flow rounded-full blur-3xl animate-float" style={{animationDelay: '2s'}}></div>
          </div>

          <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
            <div className="text-center mb-12 sm:mb-16">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-neutral-charcoal mb-4 sm:mb-6 leading-tight">
                Join <span className="bg-ember-gradient bg-clip-text text-transparent">EMBER</span>
              </h1>
              <p className="text-lg sm:text-xl text-neutral-medium max-w-4xl mx-auto leading-relaxed px-4">
                Be the first to access our transformational leadership programs. Select the waitlists that interest you most 
                and we'll notify you as soon as they become available.
              </p>
            </div>
          </div>
        </section>

        {/* Program Previews - Moved to Top */}
        <section className="py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-neutral-charcoal mb-4 sm:mb-6">
                EMBER Programs
              </h2>
              <p className="text-lg sm:text-xl text-neutral-medium px-4">
                Get early access to our transformational leadership development programs
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16 px-4 sm:px-0">
              {[
                {
                  name: 'DailyGLOW™',
                  description: 'A science-backed daily reflection app that helps leaders build sustainable growth habits through the GLOW methodology.',
                  color: 'from-ember-spark to-ember-glow',
                  features: ['5 minutes daily', 'Research-backed', 'Neural pathway training']
                },
                {
                  name: 'The Crucible',
                  description: 'An 8-week peer leadership accelerator where exceptional leaders come together for transformation.',
                  color: 'from-ember-fire to-ember-heat',
                  features: ['8 weekly sessions', 'Peer accountability', 'Expert frameworks']
                },
                {
                  name: 'LIT Expeditions™',
                  description: 'Immersive 4-7 day experiences that push boundaries and catalyze breakthrough thinking.',
                  color: 'from-ember-depth to-ember-cool',
                  features: ['4-7 day intensives', 'Boundary-pushing', 'Breakthrough experiences']
                },
                {
                  name: 'Heartlines™',
                  description: 'Curated questions for meaningful conversations.',
                  color: 'from-ember-glow to-ember-fire',
                  features: ['Conversation tools', 'Meaningful dialogue', 'Connection building']
                },
                {
                  name: 'Spark Moments™',
                  description: 'Research-backed connection activities that build trust and performance in just 5-15 minutes.',
                  color: 'from-ember-spark to-ember-flame',
                  features: ['5-15 minutes', 'Team building', 'Psychological safety']
                },
                {
                  name: 'EDGE x IMPACT™',
                  description: 'Innovation mindsets and implementation framework for leaders ready to drive meaningful change.',
                  color: 'from-ember-heat to-ember-depth',
                  features: ['Innovation focus', 'Implementation tools', 'Change leadership']
                }
              ].map((program, index) => (
                <div key={program.name} className="bg-gradient-to-br from-neutral-warm to-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-neutral-light">
                  <div className={`w-16 h-16 bg-gradient-to-br ${program.color} rounded-xl mb-4 shadow-lg`}></div>
                  <h3 className="text-xl font-bold text-neutral-charcoal mb-3">{program.name}</h3>
                  <p className="text-neutral-medium mb-4 leading-relaxed">{program.description}</p>
                  <div className="space-y-2">
                    {program.features.map((feature, i) => (
                      <div key={i} className="flex items-center text-sm text-neutral-dark">
                        <div className="w-1.5 h-1.5 bg-ember-fire rounded-full mr-2"></div>
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Simple Email Signup - No Tag Selection */}
        <section id="waitlist-form" className="py-20 bg-gradient-to-br from-ember-depth to-ember-cool text-white" aria-labelledby="waitlist-heading">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 id="waitlist-heading" className="text-4xl font-bold mb-6">
                Join the EMBER Waitlist
              </h2>
              <p className="text-xl opacity-90 max-w-3xl mx-auto leading-relaxed">
                Be the first to know when our leadership development programs launch. 
                Get exclusive early access, special pricing, and leadership insights.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20 max-w-4xl mx-auto">
              {/* ConvertKit Form with EMBER styling and checkboxes */}
              <div 
                dangerouslySetInnerHTML={{
                  __html: `
                    <form action="https://app.kit.com/forms/8170299/subscriptions" class="seva-form formkit-form" method="post" data-sv-form="8170299" data-uid="8b1e942de1" data-format="inline" data-version="5" data-options='{"settings":{"after_subscribe":{"action":"message","success_message":"Success! Now check your email to confirm your subscription.","redirect_url":""},"analytics":{"google":null,"fathom":null,"facebook":null,"segment":null,"pinterest":null,"sparkloop":null,"googletagmanager":null},"modal":{"trigger":"timer","scroll_percentage":null,"timer":5,"devices":"all","show_once_every":15},"powered_by":{"show":true,"url":"https://kit.com/features/forms?utm_campaign=poweredby&utm_content=form&utm_medium=referral&utm_source=dynamic"},"recaptcha":{"enabled":false},"return_visitor":{"action":"show","custom_content":""},"slide_in":{"display_in":"bottom_right","trigger":"timer","scroll_percentage":null,"timer":5,"devices":"all","show_once_every":15},"sticky_bar":{"display_in":"top","trigger":"timer","scroll_percentage":null,"timer":5,"devices":"all","show_once_every":15}},"version":"5"}' min-width="400 500 600 700 800" role="form" aria-labelledby="waitlist-heading">
                      <div data-style="clean">
                        <ul class="formkit-alert formkit-alert-error" data-element="errors" data-group="alert" role="alert" aria-live="polite"></ul>
                        <div data-element="fields" data-stacked="false" class="seva-fields formkit-fields">
                          <div class="formkit-field">
                            <label for="email-input" class="sr-only">Email Address (required)</label>
                            <input id="email-input" class="formkit-input" name="email_address" aria-label="Email Address" aria-required="true" placeholder="Email Address" required="" type="email" style="color: white; border-color: rgba(255, 255, 255, 0.3); border-radius: 8px; font-weight: 400; background: rgba(255, 255, 255, 0.2); padding: 16px; font-size: 16px; width: 100%; min-height: 44px;">
                          </div>
                          <div class="formkit-field">
                            <div role="group" tabindex="0" aria-labelledby="programs-legend">
                              <fieldset data-group="checkboxes" class="formkit-1753" type="Custom" order="1" save_as="Tag" group="field">
                                <legend id="programs-legend" style="color: white; font-weight: 600; font-size: 18px; margin-bottom: 16px;">I want to be notified about</legend>
                                <div class="formkit-checkboxes" data-element="tags-checkboxes" data-group="checkbox" style="margin-bottom: 12px;">
                                  <input class="formkit-checkbox sr-only" id="tag-1675565566-8219046" type="checkbox" name="tags[]" value="8219046">
                                  <label for="tag-1675565566-8219046" style="color: white; font-size: 16px; cursor: pointer; display: flex; align-items: center; justify-content: flex-start; padding: 12px; background: rgba(255, 255, 255, 0.1); border-radius: 8px; margin-bottom: 8px; border: 2px solid transparent; transition: all 0.2s ease; text-align: left; min-height: 44px;" onmouseover="this.style.borderColor='rgba(255, 87, 34, 0.5)'" onmouseout="this.style.borderColor='transparent'" role="checkbox" aria-checked="false" tabindex="0">
                                    <span style="margin-left: 8px;">DailyGLOW™</span>
                                  </label>
                                </div>
                                <div class="formkit-checkboxes" data-element="tags-checkboxes" data-group="checkbox" style="margin-bottom: 12px;">
                                  <input class="formkit-checkbox sr-only" id="tag-1675565566-8219064" type="checkbox" name="tags[]" value="8219064">
                                  <label for="tag-1675565566-8219064" style="color: white; font-size: 16px; cursor: pointer; display: flex; align-items: center; justify-content: flex-start; padding: 12px; background: rgba(255, 255, 255, 0.1); border-radius: 8px; margin-bottom: 8px; border: 2px solid transparent; transition: all 0.2s ease; text-align: left; min-height: 44px;" onmouseover="this.style.borderColor='rgba(255, 87, 34, 0.5)'" onmouseout="this.style.borderColor='transparent'" role="checkbox" aria-checked="false" tabindex="0">
                                    <span style="margin-left: 8px;">EDGExIMPACT™</span>
                                  </label>
                                </div>
                                <div class="formkit-checkboxes" data-element="tags-checkboxes" data-group="checkbox" style="margin-bottom: 12px;">
                                  <input class="formkit-checkbox sr-only" id="tag-1675565566-8219054" type="checkbox" name="tags[]" value="8219054">
                                  <label for="tag-1675565566-8219054" style="color: white; font-size: 16px; cursor: pointer; display: flex; align-items: center; justify-content: flex-start; padding: 12px; background: rgba(255, 255, 255, 0.1); border-radius: 8px; margin-bottom: 8px; border: 2px solid transparent; transition: all 0.2s ease; text-align: left; min-height: 44px;" onmouseover="this.style.borderColor='rgba(255, 87, 34, 0.5)'" onmouseout="this.style.borderColor='transparent'" role="checkbox" aria-checked="false" tabindex="0">
                                    <span style="margin-left: 8px;">LIT Expeditions™</span>
                                  </label>
                                </div>
                                <div class="formkit-checkboxes" data-element="tags-checkboxes" data-group="checkbox" style="margin-bottom: 12px;">
                                  <input class="formkit-checkbox sr-only" id="tag-1675565566-8219051" type="checkbox" name="tags[]" value="8219051">
                                  <label for="tag-1675565566-8219051" style="color: white; font-size: 16px; cursor: pointer; display: flex; align-items: center; justify-content: flex-start; padding: 12px; background: rgba(255, 255, 255, 0.1); border-radius: 8px; margin-bottom: 8px; border: 2px solid transparent; transition: all 0.2s ease; text-align: left; min-height: 44px;" onmouseover="this.style.borderColor='rgba(255, 87, 34, 0.5)'" onmouseout="this.style.borderColor='transparent'" role="checkbox" aria-checked="false" tabindex="0">
                                    <span style="margin-left: 8px;">Leadership Accelerator (The Crucible)</span>
                                  </label>
                                </div>
                                <div class="formkit-checkboxes" data-element="tags-checkboxes" data-group="checkbox" style="margin-bottom: 12px;">
                                  <input class="formkit-checkbox sr-only" id="tag-1675565566-8219049" type="checkbox" name="tags[]" value="8219049">
                                  <label for="tag-1675565566-8219049" style="color: white; font-size: 16px; cursor: pointer; display: flex; align-items: center; justify-content: flex-start; padding: 12px; background: rgba(255, 255, 255, 0.1); border-radius: 8px; margin-bottom: 8px; border: 2px solid transparent; transition: all 0.2s ease; text-align: left; min-height: 44px;" onmouseover="this.style.borderColor='rgba(255, 87, 34, 0.5)'" onmouseout="this.style.borderColor='transparent'" role="checkbox" aria-checked="false" tabindex="0">
                                    <span style="margin-left: 8px;">Heartlines™</span>
                                  </label>
                                </div>
                                <div class="formkit-checkboxes" data-element="tags-checkboxes" data-group="checkbox" style="margin-bottom: 12px;">
                                  <input class="formkit-checkbox sr-only" id="tag-1675565566-8219055" type="checkbox" name="tags[]" value="8219055">
                                  <label for="tag-1675565566-8219055" style="color: white; font-size: 16px; cursor: pointer; display: flex; align-items: center; justify-content: flex-start; padding: 12px; background: rgba(255, 255, 255, 0.1); border-radius: 8px; margin-bottom: 8px; border: 2px solid transparent; transition: all 0.2s ease; text-align: left; min-height: 44px;" onmouseover="this.style.borderColor='rgba(255, 87, 34, 0.5)'" onmouseout="this.style.borderColor='transparent'" role="checkbox" aria-checked="false" tabindex="0">
                                    <span style="margin-left: 8px;">Spark Moments™</span>
                                  </label>
                                </div>
                              </fieldset>
                            </div>
                          </div>
                          <button data-element="submit" class="formkit-submit formkit-submit" style="background: linear-gradient(135deg, #FFC107, #FF5722, #E91E63); color: white; border-radius: 8px; font-weight: 600; font-size: 18px; padding: 16px 32px; border: none; cursor: pointer; transition: all 0.3s ease; width: 100%; margin-top: 16px; min-height: 44px;" aria-describedby="submit-help">
                            <div class="formkit-spinner">
                              <div></div><div></div><div></div>
                            </div>
                            <span class="">Subscribe</span>
                          </button>
                          <div id="submit-help" class="sr-only">Click to subscribe and join the waitlist for selected programs</div>
                        </div>
                        <div class="formkit-powered-by-convertkit-container">
                          <a href="https://kit.com/features/forms?utm_campaign=poweredby&utm_content=form&utm_medium=referral&utm_source=dynamic" data-element="powered-by" class="formkit-powered-by-convertkit" data-variant="light" target="_blank" rel="nofollow">Built with Kit</a>
                        </div>
                      </div>
                    <style>
                      .seva-form[data-uid="8b1e942de1"] {
                        max-width: 100%;
                      }
                      .seva-form[data-uid="8b1e942de1"] .formkit-fields {
                        display: flex;
                        flex-direction: column;
                        gap: 20px;
                      }
                      .seva-form[data-uid="8b1e942de1"] .formkit-field {
                        width: 100%;
                      }
                      .seva-form[data-uid="8b1e942de1"] .formkit-input:focus {
                        outline: none;
                        border-color: rgba(255, 255, 255, 0.8) !important;
                        box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.2);
                      }
                      .seva-form[data-uid="8b1e942de1"] .formkit-input::placeholder {
                        color: rgba(255, 255, 255, 0.7);
                      }
                      .seva-form[data-uid="8b1e942de1"] .formkit-submit:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 8px 25px rgba(255, 87, 34, 0.4);
                      }
                      .seva-form[data-uid="8b1e942de1"] .formkit-alert {
                        background: rgba(239, 68, 68, 0.1);
                        border: 1px solid rgba(239, 68, 68, 0.3);
                        border-radius: 8px;
                        color: white;
                        padding: 12px;
                        margin-bottom: 16px;
                        text-align: center;
                      }
                      .seva-form[data-uid="8b1e942de1"] .formkit-alert:empty {
                        display: none;
                      }
                      .seva-form[data-uid="8b1e942de1"] .formkit-powered-by-convertkit-container {
                        margin-top: 20px;
                        opacity: 0.7;
                      }
                      .seva-form[data-uid="8b1e942de1"] .formkit-powered-by-convertkit {
                        color: rgba(255, 255, 255, 0.8) !important;
                        font-size: 12px;
                        text-decoration: none;
                      }
                      .seva-form[data-uid="8b1e942de1"] .formkit-powered-by-convertkit:hover {
                        color: white !important;
                      }
                      .seva-form[data-uid="8b1e942de1"] .formkit-checkbox {
                        opacity: 0;
                        position: absolute;
                        pointer-events: none;
                      }
                      .seva-form[data-uid="8b1e942de1"] .formkit-checkbox:checked + label {
                        border-color: #FF5722 !important;
                        background: rgba(255, 87, 34, 0.2) !important;
                      }
                      .seva-form[data-uid="8b1e942de1"] .formkit-checkbox:checked + label::before {
                        content: "✓";
                        position: absolute;
                        left: 12px;
                        color: #FF5722;
                        font-weight: bold;
                        font-size: 18px;
                      }
                      .seva-form[data-uid="8b1e942de1"] fieldset {
                        border: none;
                        padding: 0;
                        margin: 0;
                      }
                      .seva-form[data-uid="8b1e942de1"] legend {
                        width: 100%;
                        text-align: left;
                        padding: 0;
                        margin-bottom: 16px;
                      }
                    </style>
                  </form>
                `
              }} />
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gradient-to-br from-ember-depth to-ember-cool text-white">
          <div className="max-w-6xl mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold mb-8">
              Waitlist Member Benefits
            </h2>
            <p className="text-xl opacity-90 mb-12 max-w-3xl mx-auto">
              As an EMBER waitlist member, you'll receive exclusive access and benefits
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { title: 'Early Access', desc: 'Be the first to join when programs launch' },
                { title: 'Exclusive Pricing', desc: 'Special pricing for waitlist members' },
                { title: 'Leadership Insights', desc: 'Weekly tips and research updates' },
                { title: 'Community Access', desc: 'Connect with fellow leaders before launch' }
              ].map((benefit, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <h3 className="font-bold text-lg mb-2">{benefit.title}</h3>
                  <p className="text-sm opacity-90">{benefit.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
        </main>

        <Footer />
    </>
  );
};

export default WaitlistPage;